<template>
  <b-modal
    hide-footer
    :visible="true"
    :title="challenge.id ? 'Edit Program' : 'Add Program'"
    @hidden="cancel"
    no-close-on-backdrop
  >
    <b-form @submit="submitChallenge($event)">
      <b-form-group>
        <small class="text-muted">Program Status</small>
        <b-form-select
          v-model="localChallenge.status"
          :options="statuses"
          required
        ></b-form-select>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Start Date</small>
        <b-form-input
          v-model="localChallenge.start_date"
          type="datetime-local"
          placeholder="Select start date"
          required
        ></b-form-input>
        <small class="text-muted" style="font-size: 0.7em">
          <i>*Users cannot access the course before this date</i>
        </small>
      </b-form-group>


      <b-form-group>
        <small class="text-muted">Program Type</small>
        <b-form-select
          v-model="localChallenge.challenge_type"
          :options="challengeTypes"
        ></b-form-select>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Program Name</small>
        <b-form-input
          v-model="localChallenge.title"
          type="text"
          required
          placeholder="Enter program name"
        ></b-form-input>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Program Description</small>
        <b-form-textarea
          v-model="localChallenge.description"
          type="text"
          rows="5"
          required
          placeholder="Enter program description"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Program Video</small>
        <b-form-input
          v-model="localChallenge.video"
          type="text"
          placeholder="Enter YouTube URL (optional)"
        ></b-form-input>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox v-model="localChallenge.chat">
          Include chatroom
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Program Subscription Package</small>
        <b-form-input
          v-model="localChallenge.subscription"
          type="text"
          placeholder="Enter product id (eg. prod_Msn2n85nnLHctl)"
        ></b-form-input>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Resources Link</small>
        <b-form-input
          v-model="localChallenge.resources"
          type="text"
          placeholder="Enter resources link"
        ></b-form-input>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Select Course</small>
        <b-form-select
          v-model="localChallenge.course_id"
          :options="courseOptions"
        >
          <template #first>
            <b-form-select-option :value="null">-- Select a course (optional) --</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">
          Select program leader/s<br />
          *
          <small class="text-danger"
          >Member account must have a Coach status or higher to be selectable...
          </small>
        </small>
        <v-select
          :options="challenge_leaders"
          :filterable="false"
          :multiple="true"
          @search="challengeLeadersSearch"
          v-model="localChallenge.leaders"
        >
          <template slot="no-options"> Search and select the program leader </template>

          <template slot="option" slot-scope="option">
            <div class="d-center">
              {{ option.label }}
            </div>
          </template>

          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.label }}
            </div>
          </template>
        </v-select>
      </b-form-group>

      <b-form-group>
        <small class="text-muted">Select program members</small>
        <v-select
          :options="challenge_members"
          :filterable="false"
          :multiple="true"
          @search="membersSearch"
          v-model="localChallenge.members"
        >
          <template slot="no-options"> Search and select for program members </template>

          <template slot="option" slot-scope="option">
            <div class="d-center">
              {{ option.label }}
            </div>
          </template>

          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.label }}
            </div>
          </template>
        </v-select>
      </b-form-group>

      <div class="w-100 row">
        <div class="col text-right">
          <b-button
            type="submit"
            size="md"
            variant="success"
            :disabled="loading.save"
          >
            <b-spinner small v-if="loading.save"></b-spinner>
            Save
          </b-button>

          <b-button
            size="md"
            variant="outline-secondary"
            @click="cancel()"
            :disabled="loading.save"
          >
            Cancel
          </b-button>
        </div>
      </div>

    </b-form>

    <small class="text-danger">{{ error }}</small>

  </b-modal>
</template>

<script>
import axios from "@/axios";
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  name: "EditProgram",
  components: {
    vSelect,
  },
  props: {
    challenge: {
      type: Object,
    },
    statuses: {
      type: Array
    }
  },

  data() {
    return {
      localChallenge: { ...this.challenge },
      challenge_leaders: [],
      challenge_members: [],
      courses: [],
      error: '',
      loading: { save: false },
      challengeTypes: [
        { value: 'on-demand', text: 'On-Demand' },
        { value: 'cohort-based', text: 'Cohort-Based' },
      ],
      submitDisabled: false,
    }
  },
  computed: {
    courseOptions() {
      return this.courses.map((course) => ({value: course.id, text: course.title}));
    }
  },
  methods: {
    async submitChallenge(e) {
      if (! this.validate()) {
        return;
      }

      e?.preventDefault()

      this.loading.save = true

      const response = await axios({
        url: `programs/${this.localChallenge.id ?? ''}`.replace(/\/$/, ''),
        method: this.localChallenge.id ? 'patch' : 'post',
        data: this.localChallenge,
      })

      this.loading.save = false

      if (typeof response.data === 'object' && Object.keys(response.data.errors ?? {})) {
        this.error = response.data.message;
        return;
      }
      this.$emit('closed', true)
    },
    cancel() {
      this.$emit('closed');
    },
    challengeLeadersGet(loading, search, vm) {
      axios({
        url: 'programMembersSearch',
        params: {
          search_term: search,
          type: 'challenge_leaders'
        },
      })
        .then((response) => {
          console.log(vm.options); // = response.data.items;
          this.challenge_leaders = Object.values(response.data.items ?? []);
          loading(false);
        })
        .catch(function () {
          console.log('Error: challengeLeadersGet.');
        });
    },
    membersGet(loading, search) {
      if (! search) {
        this.challenge_members = []
        return
      }

      axios({
        url: 'programMembersSearch',
        params: {
          search_term: search,
          type: 'challenge_members',
        },
      })
        .then((response) => {
          this.challenge_members = Object.values(response.data.items ?? []);
          loading(false);
        })
    },
    challengeLeadersSearch(search, loading) {
      if (search.length) {
        loading(true);
        this.challengeLeadersGet(loading, search, this);
      }
    },
    validate() {
      let valid = true;
      if (this.localChallenge.status === '' || this.localChallenge.status === 'null') {
        valid = false;
      }
      if (this.localChallenge.title === '') {
        valid = false;
      }
      if (this.localChallenge.description === '') {
        valid = false;
      }

      this.submitDisabled = !valid

      return valid
    },
    membersSearch(search, loading) {
      if (search.length) {
        loading(true);
        this.membersGet(loading, search, this);
      }
    },
    coursesGet(challengeType = null) {
      const categoryMap = { 'on-demand': 1,  'cohort-based': 2, 'meditation': 3 };
      const category = categoryMap[challengeType] ?? null;
      let _this = this;
      _this.courses = [];
      let params = new URLSearchParams();
      if (category) params.append('category', category);
      axios({
        url: 'coursesGet',
        params: params,
      })
        .then(function (response) {
          _this.courses = response.data.courses ? Object.values(response.data.courses) : [];
        })
        .catch(function () {
          console.log('Error: coursesGet.');
        });
    }
  },
  watch: {
    'localChallenge.challenge_type'(type) {
      this.localChallenge.course_id = null;
      this.coursesGet(type);
    },
  },
  created() {
    this.coursesGet(this.localChallenge.challenge_type);
  }
}
</script>


<style scoped>

</style>
